import { useMemo, useRef } from 'react';

import { createLeadTassoIdempotencyKey } from '../leadTasso/leadTassoIdempotency';
import { AcquisitionProductId } from './acquisitionProducts';

interface AcquisitionSession {
  draft: unknown;
  idempotencyKey: string;
}

export interface AcquisitionSessionStore {
  /** The draft the product was left with, or `null` for a clean compilation. */
  getDraft<TDraft>(product: AcquisitionProductId): TDraft | null;
  setDraft<TDraft>(product: AcquisitionProductId, draft: TDraft): void;
  /** The command identity of the product's current compilation. */
  getIdempotencyKey(product: AcquisitionProductId): string;
  /**
   * Issues a new identity for a compilation that is continuing. It is what an
   * edited command needs after a failed send: the same key with a different
   * payload is `409 idempotency_conflict`, so the edit becomes a new command.
   */
  rotateIdempotencyKey(product: AcquisitionProductId): string;
  /**
   * Takes back the identity of an operation restored after a reload, so the
   * retry route is called with the key that named the original command rather
   * than with the one this fresh page generated.
   */
  adoptIdempotencyKey(product: AcquisitionProductId, key: string): void;
  /** Ends a compilation: the draft is dropped and a new identity is issued. */
  reset(product: AcquisitionProductId): void;
}

/**
 * Per-product acquisition state held above the panels. Only the selected
 * product's panel is mounted, so an unfinished draft and the idempotency key
 * that names its command would die with the panel on every tab change; keeping
 * them here is what makes a tab change a change of view rather than a loss of
 * work or a second command identity for the same compilation.
 *
 * It is deliberately a ref rather than state: nothing here is rendered, and a
 * draft saved while a panel unmounts must not re-render the workspace.
 */
export const useAcquisitionSessions = (): AcquisitionSessionStore => {
  const sessions = useRef(new Map<AcquisitionProductId, AcquisitionSession>());

  return useMemo(() => {
    const sessionOf = (product: AcquisitionProductId): AcquisitionSession => {
      const existing = sessions.current.get(product);
      if (existing) return existing;

      const created: AcquisitionSession = {
        draft: null,
        idempotencyKey: createLeadTassoIdempotencyKey(),
      };
      sessions.current.set(product, created);
      return created;
    };

    return {
      getDraft: <TDraft,>(product: AcquisitionProductId) =>
        sessionOf(product).draft as TDraft | null,
      setDraft: <TDraft,>(product: AcquisitionProductId, draft: TDraft) => {
        sessionOf(product).draft = draft;
      },
      getIdempotencyKey: (product: AcquisitionProductId) =>
        sessionOf(product).idempotencyKey,
      rotateIdempotencyKey: (product: AcquisitionProductId) => {
        const session = sessionOf(product);
        session.idempotencyKey = createLeadTassoIdempotencyKey();
        return session.idempotencyKey;
      },
      adoptIdempotencyKey: (product: AcquisitionProductId, key: string) => {
        sessionOf(product).idempotencyKey = key;
      },
      reset: (product: AcquisitionProductId) => {
        sessions.current.set(product, {
          draft: null,
          idempotencyKey: createLeadTassoIdempotencyKey(),
        });
      },
    };
  }, []);
};
